/* eslint-disable max-lines */
import { observable, action, computed } from 'mobx';

import GiftService from 'services/GiftsService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  ADD_CATEGORY_ERROR,
  ADD_CATEGORY_RESPONSE,
  ADD_GIFT_ERROR,
  ADD_GIFT_RESPONSE,
  ADMIN_ONLY_GIFTS_RESPONSE,
  GET_ADMIN_CATEGORIES_LIST_RESPONSE,
  GET_ADMIN_GIFT_LIST_RESPONSE,
  GET_ADMIN_SINGLE_CATEGORY_RESPONSE,
  GET_GIFTS_BY_CATEGORIES_ERROR,
  GET_GIFTS_BY_CATEGORIES_SUCCESS,
  GET_GIFT_CATEGORIES_LIST_RESPONSE,
  GET_GIFT_LIST_RESPONSE,
  GET_QUICK_GIFTS_DATA_SUCCESS,
  GET_SINGLE_CATEGORY_RESPONSE,
  GET_SORTED_GIFTS,
  GET_SORTED_GIFTS_RESPONSE,
  GIFTING_HISTORY_RESPONSE,
  GIFTS_SEARCH_ERROR,
  GIFTS_SEARCH_RESPONSE,
  SEND_GIFT,
  SEND_GIFT_ERROR,
  SEND_GIFT_SUCCESS,
  SEND_MASS_GIFT_ERROR,
  SEND_MASS_GIFT_RESPONSE,
  SET_GOLD_GIFTS_DATA_SUCCESS,
  SINGLE_GIFT_RESPONSE,
  TMP_IMAGE_RESPONSE,
} from 'services/MessagingService/EventNames';
import APIStates from 'enum/APIStates';


const MOBILE_GIFT_ITEMS_LENGTH = 12;
const DESKTOP_GIFT_ITEMS_LENGTH = 24;

/**
 * Keeps all the information necessary for displaying the gifts
 *
 * @class GiftsStore
 */
export class GiftsStore {
  /**
   * Creates an instance of GiftsStore.
   * Hooks up to the messaging service and registers events
   * @memberof GiftsStore
   */
  constructor() {
    this.messagingService = new messagingService();
    this.giftService = GiftService;
    this.messagingService.on(GET_GIFT_CATEGORIES_LIST_RESPONSE, this._handleGiftCategoryListResponse);

    this.messagingService.on(GET_GIFT_LIST_RESPONSE, this._handleGiftListResponse);
    this.messagingService.on(GET_ADMIN_CATEGORIES_LIST_RESPONSE, this._handleAdminCategoriesResponse);
    this.messagingService.on(GET_ADMIN_GIFT_LIST_RESPONSE, this._handleAdminGiftsResponse);
    this.messagingService.on(ADD_CATEGORY_RESPONSE, this._handleAddCategoryResponse);
    this.messagingService.on(TMP_IMAGE_RESPONSE, this._handleTmpImageResponse);
    this.messagingService.on(GET_ADMIN_SINGLE_CATEGORY_RESPONSE, this._handleSingleCategoryResponse);
    this.messagingService.on(GET_GIFTS_BY_CATEGORIES_SUCCESS, this._handleGiftListByCategoryResponse);
    this.messagingService.on(ADD_GIFT_RESPONSE, this._handleAddGiftResponse);
    this.messagingService.on(SINGLE_GIFT_RESPONSE, this._handleSingleGiftResponse);
    this.messagingService.on(GET_QUICK_GIFTS_DATA_SUCCESS, this._handleQuickSendGifts);
    this.messagingService.on(GET_SORTED_GIFTS_RESPONSE, this._handleGetSortedGiftsReponse);
    this.messagingService.on(GIFTING_HISTORY_RESPONSE, this._handleGiftingHistory);
    this.messagingService.on(ADD_GIFT_ERROR, this._handleAddGiftError);
    this.messagingService.on(ADD_CATEGORY_ERROR, this._handleAddCategoryError);
    this.messagingService.on(GIFTS_SEARCH_RESPONSE, this._handleGiftsSearchResponse);
    this.messagingService.on(GIFTS_SEARCH_ERROR, this._handleGiftsSearchError);
    this.messagingService.on(GET_SINGLE_CATEGORY_RESPONSE, this._handleMarketplaceSingleCategoryResponse);
    this.messagingService.on(SET_GOLD_GIFTS_DATA_SUCCESS, this._setGoldGifts);
    this.messagingService.on(SEND_GIFT_SUCCESS, this._handleSendGiftSuccess);
    this.messagingService.on(SEND_GIFT_ERROR, this._handleSendGiftError);
    this.messagingService.on(GET_GIFTS_BY_CATEGORIES_ERROR, this._handleGetGiftsError);
    this.messagingService.on(ADMIN_ONLY_GIFTS_RESPONSE, this._handleAdminOnlyGiftsResponse);
    this.messagingService.on(SEND_MASS_GIFT_RESPONSE, this._handleMassGiftsResponse);
    this.messagingService.on(SEND_MASS_GIFT_ERROR, this._handleMassGiftsError);
  }

  /**
   * Unbind events
   * @memberof GiftsStore
   */
  destructor() {
    this.messagingService.off(GET_GIFT_CATEGORIES_LIST_RESPONSE, this._handleGiftCategoryListResponse);
    this.messagingService.off(GET_GIFT_LIST_RESPONSE, this._handleGiftListResponse);
    this.messagingService.off(GET_ADMIN_CATEGORIES_LIST_RESPONSE, this._handleAdminCategoriesResponse);
    this.messagingService.off(GET_ADMIN_GIFT_LIST_RESPONSE, this._handleAdminGiftsResponse);
    this.messagingService.off(ADD_CATEGORY_RESPONSE, this._handleAddCategoryResponse);
    this.messagingService.off(TMP_IMAGE_RESPONSE, this._handleTmpImageResponse);
    this.messagingService.off(GET_ADMIN_SINGLE_CATEGORY_RESPONSE, this._handleSingleCategoryResponse);
    this.messagingService.off(GET_GIFTS_BY_CATEGORIES_SUCCESS, this._handleGiftListByCategoryResponse);
    this.messagingService.off(SINGLE_GIFT_RESPONSE, this._handleSingleGiftResponse);
    this.messagingService.off(GET_QUICK_GIFTS_DATA_SUCCESS, this._handleQuickSendGifts);
    this.messagingService.off(GET_SORTED_GIFTS_RESPONSE, this._handleGetSortedGiftsReponse);
    this.messagingService.off(GIFTING_HISTORY_RESPONSE, this._handleGiftingHistory);
    this.messagingService.off(ADD_GIFT_ERROR, this._handleAddGiftError);
    this.messagingService.off(ADD_CATEGORY_ERROR, this._handleAddCategoryError);
    this.messagingService.off(GIFTS_SEARCH_RESPONSE, this._handleGiftsSearchResponse);
    this.messagingService.off(GIFTS_SEARCH_ERROR, this._handleGiftsSearchError);
    this.messagingService.off(GET_SINGLE_CATEGORY_RESPONSE, this._handleMarketplaceSingleCategoryResponse);
    this.messagingService.off(SET_GOLD_GIFTS_DATA_SUCCESS, this._setGoldGifts);
    this.messagingService.off(SEND_GIFT_SUCCESS, this._handleSendGiftSuccess);
    this.messagingService.off(SEND_GIFT_ERROR, this._handleSendGiftError);
    this.messagingService.off(GET_GIFTS_BY_CATEGORIES_ERROR, this._handleGetGiftsError);
    this.messagingService.off(ADMIN_ONLY_GIFTS_RESPONSE, this._handleAdminOnlyGiftsResponse);
    this.messagingService.off(SEND_MASS_GIFT_RESPONSE, this._handleMassGiftsResponse);
    this.messagingService.off(SEND_MASS_GIFT_ERROR, this._handleMassGiftsError);
  }

  @observable gifts = [];
  @observable giftsReqInfo = {};
  @observable categories = [];
  @observable adminGifts = [];
  @observable adminCategories = [];
  @observable openedGift = null;
  @observable tmpImageData = {};
  @observable addCategoryStatus = APIStates.INITIAL;
  @observable addGiftStatus = APIStates.INITIAL;
  @observable getAdminGiftsStatus = APIStates.INITIAL;
  @observable totalPagesAdmin = 0;
  @observable giftsByCategory = [];
  @observable activeCategoryFilter = null;
  @observable searchTerm = null;
  @observable quickGiftsData = null;
  @observable sortedGifts = [];
  @observable giftingHistory = {};
  @observable totalPages = 0;
  @observable giftsSearchResults = [];
  @observable searchGiftsStatus = APIStates.INITIAL;
  @observable singleCategoryGifts = [];
  @observable searchValue = '';
  @observable searchFieldVisible = false;
  @observable sendGiftStatus = APIStates.INITIAL;
  @observable sendGiftErrorCode = '';
  @observable getGiftsStatus = APIStates.INITIAL;
  @observable adminOnlyGifts = [];
  @observable sendMassGiftStatus = APIStates.INITIAL;

  /**
   * Method for setting search field value
   * @param {String} value - search term
   */
  @action setSearchValue = (value) => {
    this.searchValue = value;
  };

  /**
   * Method for showing or hiding search field
   * @param {Boolean} flag - true or false
   */
  @action setSearchFieldVisible = (flag) => {
    this.searchFieldVisible = flag;
  };

  /**
   * Prepares sorted gift list for desktop
   *
   * @readonly
   * @returns {Array}
   */
  @computed get sortedGiftsListDesktop() {
    const sortedGifts = this.gifts.slice(0, DESKTOP_GIFT_ITEMS_LENGTH);
    if (sortedGifts.length < DESKTOP_GIFT_ITEMS_LENGTH) {
      const totalSortedGifts = sortedGifts.concat(new Array(DESKTOP_GIFT_ITEMS_LENGTH - sortedGifts.length).fill(0));

      return totalSortedGifts;
    }

    return sortedGifts;

  }

  /**
   * Prepares sorted gift list for mobile
   *
   * @readonly
   * @returns {Array}
   */
  @computed get sortedGiftsListMobile() {
    const sortedGifts = this.gifts.slice(0, MOBILE_GIFT_ITEMS_LENGTH);
    if (sortedGifts.length < MOBILE_GIFT_ITEMS_LENGTH) {
      const totalSortedGifts = sortedGifts.concat(new Array(MOBILE_GIFT_ITEMS_LENGTH - sortedGifts.length).fill(0));

      return totalSortedGifts;
    }

    return sortedGifts;

  }

  /**
   * Prepares filtered gift categories
   *
   * @readonly
   * @returns {Array}
   */
  @computed get filteredCategories() {
    if (this.activeCategoryFilter) {
      return this.giftsByCategory.filter((cat) => cat.id === this.activeCategoryFilter);
    }

    return this.giftsByCategory;

  }

  /**
   * Check if there are more gifts to load for desktop
   *
   * @readonly
   * @returns {Boolean}
   */
  @computed get hasMoreThanLimit() {
    return this.giftsReqInfo.totalElements > DESKTOP_GIFT_ITEMS_LENGTH;
  }

  /**
   * Check if there are more gifts to load for mobile
   *
   * @readonly
   * @returns {Boolean}
   */
  @computed get hasMoreThanLimitMobile() {
    return this.giftsReqInfo.totalElements > MOBILE_GIFT_ITEMS_LENGTH;
  }

  /**
   * Method for setting gift category
   * @param {String} id - gift id
   */
  @action setCategoryFilter = (id) => {
    this.activeCategoryFilter = id;
  };

  /**
   * Method for getting sorted gifts
   * @param {String} sortOption - sort option
   * @param {Number} page - page number
   */
  @action getSortedGifts = (sortOption, page) => {
    this.messagingService.emit(GET_SORTED_GIFTS, { sortOption, page });
  };

  /**
   * Method for setting search term
   * @param {String} term
   */
  @action setSearchTerm = (term) => {
    this.searchTerm = term;
  };

  /**
   * Private method for handling gift categories response
   * @param {Object} data - data from api response
   */
  @action _handleGiftCategoryListResponse = (data) => {
    this.categories = data;
  };

  /**
   * Private method for handling admin gifts response
   * @param {Object} data - data from api response
   */
  @action _handleAdminGiftsResponse = (data) => {
    this.adminGifts = data.content;
    this.totalPagesAdmin = data.totalPages;
    this.getAdminGiftsStatus = APIStates.LOADED;
  };

  /**
   * Private method for handling admin gift categories response
   * @param {Object} data - data from api response
   */
  @action _handleAdminCategoriesResponse = (data) => {
    this.adminCategories = data;
  };

  /**
   * Private method for handling response after adding new category
   */
  @action _handleAddCategoryResponse = () => {
    this.addCategoryStatus = APIStates.LOADED;
  };

  /**
   * Private method for handling response after adding new gift
   */
  @action _handleAddGiftResponse = () => {
    this.addGiftStatus = APIStates.LOADED;
    this.tmpImageData = {};
  };

  /**
   * Private method for handling response after adding gift image
   * @param {Object} data - data from api response
   */
  @action _handleTmpImageResponse = (data) => {
    this.tmpImageData = data;
  };

  /**
   * Private method for handling sorted gifts response
   * @param {Object} data - data from api response
   */
  @action _handleGetSortedGiftsReponse = (data) => {
    if (data.content) {
      this.sortedGifts = data.content;
      this.totalPages = data.totalPages;
    } else {
      this.sortedGifts = data;
    }
  };

  /**
   * Method for clearing sorted gifts
   */
  @action clearSortedGifts = () => {
    this.sortedGifts = [];
  };

  /**
   * Private method for handling single category admin gifts response
   * @param {Object} data - data from api response
   */
  @action _handleSingleCategoryResponse = (data) => {
    this.adminGifts = [data];
    this.totalPagesAdmin = 0;
  };

  /**
   * Method for clearing admin gifts
   */
  @action clearAdminGifts = () => {
    this.adminGifts = [];
  };

  /**
   * Private method for handling single category gifts response
   * @param {Object} data - data from api response
   */
  @action _handleMarketplaceSingleCategoryResponse = (data) => {
    this.singleCategoryGifts = data.gifts ? data.gifts : [];
    this.totalPages = 0;
  };

  /**
   * Private method for handling single admin gift response
   * @param {Object} data - data from api response
   */
  @action _handleSingleGiftResponse = (data) => {
    this.openedGift = data;
  };

  /**
   * Private method for handling gifts by category response
   * @param {Object} data - data from api response
   */
  @action _handleGiftListByCategoryResponse = (data) => {
    this.giftsByCategory = data.content;
    this.totalPages = data.totalPages;
    this.getGiftsStatus = APIStates.LOADED;
  };

  /**
   * Private method for handling gift list response
   * @param {Object} data - data from api response
   */
  @action _handleGiftListResponse = (data) => {
    const { content, ...rest } = data;
    const parsedGifts = content.map((item) => {
      return {
      // This mapping is done to accomodate the BE inconsistencies.
        ...item.gift,
        sender: item.sender,
        senderType: item.senderType,
        senderId: item.id,
        price: item.price,
        dateSent: item.dateSent,
        ttKey: item.ttKey,
      };
    });
    this.gifts =
      rest.number !== this.giftsReqInfo.number && rest.number !== 0 ? [...this.gifts, ...parsedGifts] : parsedGifts;
    this.giftsReqInfo = rest;
  };

  /**
   * Method for sending gift
   * @param {Object} giftDetails - giftId, recipient, message, anonymous
   */
  @action sendGift = (giftDetails) => {
    this.messagingService.emit(SEND_GIFT, giftDetails);
    this.sendGiftStatus = APIStates.PENDING;
  };

  /**
   * Private method for handling successful gift sending
   */
  @action _handleSendGiftSuccess = () => {
    this.sendGiftStatus = APIStates.LOADED;
  };

  /**
   * Private method for handling gift sending error
   * @param {Object} error
   */
  @action _handleSendGiftError = (error) => {
    this.sendGiftErrorCode = error.response.data && error.response.data.errorCode;
    this.sendGiftStatus = APIStates.ERROR;
  };

  /**
   * Method for getting gifts from single category
   * @param {Object} params - page number
   */
  @action getGiftByCategories = (params) => {
    GiftService.getGiftsByCategory(params);
  };

  /**
   * Method for reseting send gift status
   */
  @action resetSendGiftStatus = () => {
    this.sendGiftStatus = APIStates.INITIAL;
  };

  /**
   * Method for getting gift list
   * @param {String} performerName - performer name
   * @param {Object} params - size, page number
   */
  @action getGiftList = (performerName, params) => {
    GiftService.getGiftList(performerName, params);
  };

  /**
   * Method for getting list of cateries
   */
  @action getGiftCategoryList = () => {
    GiftService.getGiftCategoryList();
  };

  @action getQuickGifts = (username) => {
    GiftService.getQuickGifts(username);
  };

  /**
   * Method for getting admin gifts by category
   * @param {Object} params - size, page number
   */
  @action getAdminGiftsByCategory = (params) => {
    this.getAdminGiftsStatus = APIStates.PENDING;
    GiftService.getAdminGiftsByCategory(params);
  };

  /**
   * Method for getting sorted admin gifts
   * @param {Object} params - sort option, page number
   */
  @action getAdminGifts = (params) => {
    this.getAdminGiftsStatus = APIStates.PENDING;
    GiftService.getAdminGifts(params);
  };

  /**
   * Method for getting admin categories list
   */
  getAdminCategoryList = () => {
    GiftService.getAdminCategoryList();
  };

  /**
   * Method for getting admin gifts from single category
   * @param {String} categoryId - category id
   * @param {Object} params - sort option
   */
  getSingleCategory = (categoryId, params) => {
    GiftService.getSingleCategory(categoryId, params);
  };

  /**
   * Method for getting marketplace gifts from single category
   * @param {String} categoryId - category id
   * @param {Object} params - sort option
   */
  getMarketplaceSingleCategory = (categoryId, params) => {
    GiftService.getMarketplaceSingleCategory(categoryId, params);
  };

  /**
   * Method for getting single gift
   * @param {String} giftId - gift id
   */
  getSingleGift = (giftId) => {
    GiftService.getSingleGift(giftId);
  };

  /**
   * Method for adding new gift
   * @param {Object} data - gift details
   * @param {Object} params - sort option, page number
   */
  @action addNewGift = (data, params) => {
    this.addGiftStatus = APIStates.PENDING;
    GiftService.addNewGift(data, params);
  };

  /**
   * Method for editing gift
   * @param {String} giftId - gift id
   * @param {Object} data - gift details
   * @param {Object} params - sort option, page number
   */
  @action updateGift = (giftId, data, params) => {
    this.addGiftStatus = APIStates.PENDING;
    GiftService.updateGift(giftId, data, params, this.searchValue);
  };

  /**
   * Method for clearing gift previously opened in modal
   */
  @action resetOpenedGift = () => {
    this.openedGift = null;
  };

  /**
   * Method for adding new git category
   * @param {Object} data - category details
   * @param {Object} params - sort option, page number
   */
  @action addNewCategory = (data, params) => {
    this.addCategoryStatus = APIStates.PENDING;
    GiftService.addNewCategory(data, params);
  };

  /**
   * Method for editing category
   * @param {String} categoryId - category id
   * @param {Object} data - gift details
   * @param {Object} params - sort option, page number
   */
  @action updateCategory = (categoryid, data, params) => {
    this.addCategoryStatus = APIStates.PENDING;
    GiftService.updateCategory(categoryid, data, params);
  };

  /**
   * Method for seting category adding status
   * @param {String} status - INITIAL, PENDING, ERROR
   */
  @action resetAddCategoryStatus = (status) => {
    this.addCategoryStatus = status;
  };

  /**
   * Method for seting gift adding status
   * @param {String} status - INITIAL, PENDING, ERROR
   */
  @action resetAddGiftStatus = (status) => {
    this.addGiftStatus = status;
  };

  @computed get giftAdded() {
    return this.addGiftStatus === APIStates.LOADED;
  }

  @computed get categoryAdded() {
    return this.addCategoryStatus === APIStates.LOADED;
  }

  /**
   * Method for getting temporary gift image data
   * @param {Object} params - image name
   */
  getTmpImageData = (params) => {
    GiftService.getTmpImageData(params);
  };

  /**
   * Method for uploading gift image to CDN
   * @param {Object} image - image
   */
  uploadImageToCdn = (image) => {
    GiftService.uploadImageToCdn(this.tmpImageData.temporaryUploadUrl, image);
  };

  /**
   * Method for getting gifting history
   * @param {Object} params - order, page, perPage
   * @param {Object | string} user - user object or username string
   */
  getGiftingHistory = (params, user) => {
    if (typeof user === 'string') {
      GiftService.getGiftingHistory(params, user);
    } else if (user.username) {
      GiftService.getGiftingHistory(params, user.username);
    }
  };

  /**
   * Method for searching gifts
   * @param {Object} params -  search text
   */
  searchGifts = (params) => {
    this.searchGiftsStatus = APIStates.PENDING;
    this.giftsSearchResults = [];
    GiftService.searchGifts(params);
  };

  @action _handleQuickSendGifts = (data) => {
    this.quickGiftsData = data;
  };

  @action _handleGiftingHistory = (data) => {
    this.giftingHistory = data;
  };

  @action _handleAddGiftError = () => {
    this.addGiftStatus = APIStates.ERROR;
  };

  @action _handleAddCategoryError = () => {
    this.addCategoryStatus = APIStates.ERROR;
  };

  @action _handleGiftsSearchResponse = (data) => {
    this.searchGiftsStatus = APIStates.LOADED;
    this.giftsSearchResults = data ? data : [];
  };

  @action _handleGiftsSearchError = () => {
    this.searchGiftsStatus = APIStates.ERROR;
  };

  @action _handleGetGiftsError = () => {
    this.getGiftsStatus = APIStates.ERROR;
  };

  /**
   * Method for clearing search results
   */
  @action clearSearchResults = () => {
    this.giftsSearchResults = [];
    this.searchGiftsStatus = APIStates.INITIAL;
  };

  /**
   * Method for getting gold gifts
   */
  getGoldGifts = () => {
    GiftService.getGoldGifts();
  };

  /**
   * Private method for handling gold gifts response
   * @param {Object} gold - response
   */
  @action _setGoldGifts = (gold) => {
    this.quickGiftsData = {
      ...this.quickGiftsData,
      gold,
    };
  };

  /**
   * Method for sending mass gifts
   * @param {Object} data - gift id, message, price, recipients
   */
  @action sendMassGift = (data) => {
    this.sendMassGiftStatus = APIStates.PENDING;
    GiftService.sendMassGift(data);
  };

  /**
   * Method for getting admin only gifts
   */
  getAdminOnlyGifts = () => {
    GiftService.getAdminOnlyGifts();
  };

  /**
   * Private method for handling admin only gifts response
   * @param {Object} data - response
   */
  @action _handleAdminOnlyGiftsResponse = (data) => {
    this.adminOnlyGifts = data;
  };

  /**
   * Private method for successful mass gift sending
   */
  @action _handleMassGiftsResponse = () => {
    this.sendMassGiftStatus = APIStates.LOADED;
  };

  /**
   * Private method for error when sending mass gifts
   */
  @action _handleMassGiftsError = () => {
    this.sendMassGiftStatus = APIStates.ERROR;
  };

  /**
   * Checks if mass gifts are being sent
   *
   * @readonly
   * @returns {Boolean}
   */
  @computed get sendMassGiftsPending() {
    return this.sendMassGiftStatus === APIStates.PENDING;
  }

  /**
   * Checks if the user initiated gifts search
   *
   * @readonly
   * @returns {Boolean}
   */
  @computed get searchInitiated() {
    return this.searchGiftsStatus !== APIStates.INITIAL;
  }

  /**
   * Checks if the gift search is pending
   *
   * @readonly
   * @returns {Boolean}
   */
  @computed get searchPending() {
    return this.searchGiftsStatus === APIStates.PENDING;
  }

  /**
   * Checks if gift search has failed
   *
   * @readonly
   * @returns {Boolean}
   */
  @computed get searchError() {
    return this.searchGiftsStatus === APIStates.ERROR;
  }

  /**
   * Checks if there are no search results
   *
   * @readonly
   * @returns {Boolean}
   */
  @computed get noSearchResults() {
    return this.searchGiftsStatus === APIStates.LOADED && this.giftsSearchResults.length < 1;
  }

  /**
   * Checks if the gift categories are not emtpy
   *
   * @readonly
   * @returns {Boolean}
   */
  @computed get categoriesFetched() {
    return this.categories.length > 0;
  }

  /**
   * Checks if the gift is being sent
   *
   * @readonly
   * @returns {Boolean}
   */
  @computed get giftSendPending() {
    return this.sendGiftStatus === APIStates.PENDING;
  }

  /**
   * Checks if the gift was sent
   *
   * @readonly
   * @returns {Boolean}
   */
  @computed get giftSendSuccessful() {
    return this.sendGiftStatus === APIStates.LOADED;
  }

  /**
   * Checks if the gift was not sent
   *
   * @readonly
   * @returns {Boolean}
   */
  @computed get giftSendError() {
    return this.sendGiftStatus === APIStates.ERROR;
  }

  /**
   * Checks if the gifts failed to load
   *
   * @readonly
   * @returns {Boolean}
   */
  @computed get giftsNotLoaded() {
    return this.getGiftsStatus === APIStates.ERROR;
  }
}

export default new GiftsStore();
